/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';
import Loading from 'components/Loading/Loading';
import { Roles, RoleMap } from 'permissions/defs';
import { customerUsers } from 'permissions/userTypes';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import ChangeUserRoleForm from './ChangeUserRoleForm/ChangeUserRoleForm';
import ChangeUserRoleSuccess from './ChangeUserRoleSuccess/ChangeUserRoleSuccess';
import ChangeUserRoleError from './ChangeUserRoleError/ChangeUserRoleError';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  formData: Object,
  currentAccountDetails: Object,
  actionDrawerStore: TypeActionDrawerStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class ChangeUserRole extends React.Component<*> {
  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { currentAccountDetails, actionDrawerStore }: TypeProps = this.props;
    const formData: Object = this.props.formData;
    const fields: Array<Object> = [
      {
        name: 'changeUserRoleForm',
        label: t('general:actions.change_user_role'),
        title: 'Change User Role',
        fields: [
          {
            name: 'reason',
            label: t('general:reasons.reason_for_changing_access'),
            rules: 'required|string',
            value: '',
            type: 'select',
            options: [
              {
                id: 1,
                label: t('general:reasons.options.change_in_role_resposibilities'),
              },
              {
                id: 'other',
                label: t('general:reasons.options.other'),
              },
            ],
          },
          {
            key: 'userRole',
            type: 'radiogroup',
            title: '',
            name: 'userRole',
            rules: 'numeric',
            label: '',
            value: formData.role_id,
            options: customerUsers.map(role => ({
              id: role,
              label: t(`general:roles.${role}`),
              value: role,
              hint: t(`general:roles.descriptions.${role}`),
            })),
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const { reason, userRole } = data.values().changeUserRoleForm;

        actionDrawerStore.changeUserRole(currentAccountDetails.id, {
          user_id: formData.id,
          role: RoleMap[userRole],
          reason,
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: 'Change User Role',
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { formData, currentAccountDetails, actionDrawerStore }: TypeProps = this.props;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.change_user_role')} />
          <ChangeUserRoleError user={formData} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={t('general:actions.change_user_role')} />
          <ChangeUserRoleSuccess user={formData} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.change_user_role')} />
        <ChangeUserRoleForm
          user={formData}
          currentAccountDetails={currentAccountDetails}
          form={this.createForm()}
        />
      </div>
    );
  }
}
